import {useEffect, useRef, useState} from 'react'
import {Box, ClickAwayListener, Grid, MenuItem} from '@mui/material'
import LineChart from '../charts/lineChart'
import {useHealthAndEquity} from '../../store/hooks/healthAndEquityCondition'

import ChartDataRefetchOnError from '../common/ChartDataRefetchOnError'
import ChartBlankError from '../common/ChartBlankError'
import ChartLoadingIndicator from '../common/ChartLoadingIndicator'
import ChartTitle from '../common/ChartTitle'
import ColorSlider from '../common/ColorSlider/ColorSlider'
import {useLongitudinalStates} from '../../store/hooks/longitudinalStates'
import {useLongitudinalRiskChart} from '../../store/hooks/longitudinalRiskChart'
import {useQueryCountyState, useQueryAllCounties} from '../../store/hooks/longitudinalStateCounty'
import {StyledSelect, StyledFormControl, StyledBox} from './RiskOverTimeComponentStyle'

import {RISK_COLOR, CHART_NAMES} from '../Config/config'
import {generateData, generateLabels, checkIsChartResponseEmpty, generateAriaLabelForChart} from '../common/helpers'
import {ChartActionButtons} from '../common/ChartActionButtons'
import {useTableViewContext} from '../../store/hooks/tableView'
import TimeSeriesTable from '../common/TimeSeriesTable'
import ToggleButton from '../common/ToggleButton'
import GraphTooltip from '../charts/GraphTooltip'
import MarkerTooltip from '../charts/MarkerTooltip/MarkerTooltip'
import {handleClickChartOutside} from '../common/markerHelpers'

const titleInit = 'Risk Over Time'
const chartData = new Array(24).fill(null)

const RiskOverTimeComponent = () => {
  const riskChartRef = useRef()
  const [zoomProps, setZoomProps] = useState({reset: true, in: false, out: true})
  const [clickAwayEnabled, setClickAwayEnabled] = useState(false)
  const [tooltipVisible, setTooltipVisible] = useState(false)
  const [showMarkerTooltip, setShowMarkerTooltip] = useState(false)
  const [selectedState, setSelectedState] = useState('')
  const [selectedCounty, setSelectedCounty] = useState('')
  const [selectedHealthCondition, setSelectedHealthCondition] = useState('')
  const {states, statesList, updateDefaultState, updateDefaultChartType, updateDefaultCounties} =
    useLongitudinalStates()
  const [queryCounty, {county}] = useQueryCountyState()
  const [{allCounty}] = useQueryAllCounties()
  const [showLoader, setShowLoader] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [title, setTitle] = useState(titleInit)
  const [yMax, setYMax] = useState(0)
  const [yMin, setYMin] = useState(0)
  const [xLabels, setXLabels] = useState([])
  const [xDates, setXDates] = useState([])
  const [dataSets, setDataSets] = useState([])
  const [displayLegend, setDisplayLegend] = useState(false)
  const [sliderValue, setSliderValue] = useState([0, 100])
  const [sliderChangingValue, setSliderChangingValue] = useState([0, 100])
  const [chartArialabel, setChartArialabel] = useState('Risk Over Time Chart per 100k')
  const [currentSelections, setCurrentSelections] = useState({})
  const {healthAndEquityConditions, loadingHealthAndEquityConditions, errorLoadingHealthAndEquityConditions} =
    useHealthAndEquity()
  const {
    getChartNationData,
    chartNationData,
    getChartStateData,
    chartStateData,
    getChartCountyData,
    chartCountyData,
    setChartVariables
  } = useLongitudinalRiskChart()
  const {showRiskOverTimeTable, updateToggleRiskOverTime} = useTableViewContext()
  const concatData = (data1, data2) => {
    const data = []
    if (data1 && data2) {
      data.push(data1, data2)
    }
    return data
  }

  const checkDataSelected = () => {
    let data1 = []
    let data2 = []
    let legend1 = ''
    let legend2 = ''
    if (selectedState !== '' && selectedCounty !== '' && selectedHealthCondition !== '') {
      data1 = chartStateData?.chartData?.heRiskState
      legend1 = 'State Average'
      data2 = chartCountyData?.chartData?.heRiskCounty
      const county = statesList[0].currentRiskOverTimeCounties?.longitudinalCountyList?.find(
        (county) => county.STATE_COUNTY_FIPS_CODE === selectedCounty
      )
      if (county) legend2 = county.COUNTY_NAME
    }
    if (selectedState !== '' && selectedCounty === '' && selectedHealthCondition !== '') {
      data1 = chartNationData?.chartData?.heRiskNation
      legend1 = 'National Average'
      data2 = chartStateData?.chartData?.heRiskState
      const state = states?.longitudinalStateList?.find((state) => state.STATE_ABBREVIATION === selectedState)
      if (state) legend2 = state.STATE_NAME
    }
    return [data1, data2, legend1, legend2]
  }

  const generateDataSet = () => {
    const [data1, data2, legend1, legend2] = checkDataSelected()
    const concatedData = concatData(data1, data2)
    const [xLables, xLablesWithDates, yMin, yMax] = generateLabels(concatedData, 'COUNT_PER_100K')
    const dataSets = [
      {
        label: legend1,
        data: data1 && data1?.length > 0 ? generateData(data1, xLables, 'COUNT_PER_100K') : chartData,
        borderDash: [5, 5],
        borderColor: data1?.length > 0 ? RISK_COLOR[0] : 'transparent',
        backgroundColor: data1?.length > 0 ? RISK_COLOR[0] : 'transparent',
        spanGaps: true
      },
      {
        label: legend2,
        data: data2 && data2?.length > 0 ? generateData(data2, xLables, 'COUNT_PER_100K') : chartData,
        borderColor: data1?.length > 0 ? RISK_COLOR[1] : 'transparent',
        backgroundColor: data1?.length > 0 ? RISK_COLOR[1] : 'transparent',
        spanGaps: true
      }
    ]
    concatedData.length > 0 && setDataSets(dataSets)
    setXLabels(xLables)
    setYMax(yMax)
    setYMin(yMin)
    setXDates(xLablesWithDates)
  }

  const handleChangeState = (event) => {
    const newState = event.target.value
    setSelectedState(newState)
    setSelectedCounty('')
    updateDefaultChartType('RiskOverTimeChart')
    updateDefaultState('RiskOverTimeChart', newState)
  }

  const handleChangeCounty = (event) => {
    const newCounty = event.target.value
    setSelectedCounty(newCounty)
  }

  const handleChangeHealthCondition = (event, item) => {
    setSelectedHealthCondition(event.target.value)
    const condition = item.props.children
    setTitle(`${condition} Risk Over Time`)
    setSliderValue([0, 100])
    setSliderChangingValue([0, 100])
  }

  const fetchChartData = () => {
    if (selectedHealthCondition !== '') {
      let updatedVariables = {}
      updatedVariables = {
        healthKey: selectedHealthCondition,
        minRisk: sliderValue[0],
        maxRisk: sliderValue[1],
        stateCode: selectedState,
        stateCountyCode: selectedCounty
      }
      setChartVariables(updatedVariables)
    }
    if (selectedState !== '' && selectedHealthCondition !== '' && selectedCounty === '') {
      getChartNationData()
      getChartStateData()
      setDisplayLegend(true)
    }
    if (selectedState !== '' && selectedCounty !== '' && selectedHealthCondition !== '') {
      getChartStateData()
      getChartCountyData()
      setDisplayLegend(true)
    }
  }
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: 260
      }
    }
  }

  useEffect(() => {
    if (selectedState) {
      queryCounty({variables: {state: selectedState}})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState])

  useEffect(() => {
    if (statesList[0].chartType === 'RiskOverTimeChart' && county?.longitudinalCountyList?.length > 0) {
      updateDefaultCounties(statesList[0].chartType, county || allCounty)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statesList, county, allCounty])

  useEffect(() => {
    fetchChartData()
    if (selectedHealthCondition !== '') {
      const {STATE_NAME: stateName} =
        states?.longitudinalStateList?.find((o) => o.STATE_ABBREVIATION === selectedState) ?? {}
      let {COUNTY_NAME: countyName} =
        statesList[0].currentRiskOverTimeCounties?.longitudinalCountyList?.find(
          (o) => o.STATE_COUNTY_FIPS_CODE === selectedCounty
        ) ?? {}
      const {HEALTH_EQUITY_CONDITION_NAME: healthConditionName} =
        healthAndEquityConditions?.healthEquityConditonList?.find(
          (o) => o.HEALTH_EQUITY_CONDITION_KEY === selectedHealthCondition
        ) ?? {}
      if (!countyName) countyName = 'All Counties'
      setCurrentSelections({state_name: stateName, county_name: countyName, healthCondition_name: healthConditionName})
      setChartArialabel(() => generateAriaLabelForChart(healthConditionName, countyName, stateName, sliderValue))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState, selectedCounty, selectedHealthCondition, sliderValue])

  useEffect(() => {
    setIsLoading(() => chartStateData.loadingChart || chartNationData.loadingChart || chartCountyData.loadingChart)
  }, [chartStateData.loadingChart, chartNationData.loadingChart, chartCountyData.loadingChart])
  useEffect(() => {
    if (isLoading) setShowLoader(isLoading)
  }, [isLoading])
  useEffect(() => {
    setIsError(
      () => chartStateData.errorLoadingChart || chartNationData.errorLoadingChart || chartCountyData.errorLoadingChart
    )
  }, [chartStateData.errorLoadingChart, chartNationData.errorLoadingChart, chartCountyData.errorLoadingChart])

  useEffect(() => {
    generateDataSet()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartNationData, chartStateData, chartCountyData])

  const handleSliderChangeCommit = () => {
    if (sliderChangingValue[0] === sliderValue[0] && sliderChangingValue[1] === sliderValue[1]) return
    setSliderValue(sliderChangingValue)
  }
  const handleSliderChange = (e, newValue, activeThumb) => {
    if (activeThumb === 0)
      setSliderChangingValue([Math.min(newValue[0], sliderChangingValue[1] - 10), sliderChangingValue[1]])
    else setSliderChangingValue([sliderChangingValue[0], Math.max(newValue[1], sliderChangingValue[0] + 10)])
  }
  const handelTooltip = (data) => {
    const tooltip = []
    for (const dataItem of data) {
      const title = dataItem.dataset.label
      let condition = ''
      let RatePer100K = dataItem.formattedValue
      RatePer100K = RatePer100K.replaceAll(/\.(\d*)/g, '')
      const date = xDates[dataItem.dataIndex]
      const risk = healthAndEquityConditions?.healthEquityConditonList?.find(
        (risk) => risk.HEALTH_EQUITY_CONDITION_KEY === selectedHealthCondition
      )
      if (risk) condition = risk.HEALTH_EQUITY_CONDITION_NAME
      tooltip.push([{[title]: ''}, {Condition: condition}, {'Rate Per 100K': RatePer100K}, {Date: date}])
    }
    return tooltip
  }

  const conditionalRender = () => {
    if (!isError && !showLoader && !checkIsChartResponseEmpty(dataSets)) {
      if (showRiskOverTimeTable)
        return (
          <TimeSeriesTable
            dataSets={dataSets}
            xDates={xDates}
            displayLegend={displayLegend}
            currentSelections={currentSelections}
          />
        )
      else
        return (
          <Box sx={{height: '100%'}} aria-label={chartArialabel} position='relative'>
            <ClickAwayListener
              mouseEvent={clickAwayEnabled ? 'onClick' : false}
              onClickAway={() => handleClickChartOutside(riskChartRef.current)}
              touchEvent={clickAwayEnabled ? 'onTouchEnd' : false}
            >
              <Box>
                <LineChart
                  dataSets={dataSets}
                  yMax={yMax}
                  yMin={yMin}
                  chartRef={riskChartRef}
                  xLabels={xLabels}
                  setZoomProps={setZoomProps}
                  displayLegend={displayLegend}
                  name={CHART_NAMES.RISK}
                  setTooltipVisible={setTooltipVisible}
                  setShowMarkerTooltip={setShowMarkerTooltip}
                  setClickAwayEnabled={setClickAwayEnabled}
                />
              </Box>
            </ClickAwayListener>
            {riskChartRef?.current?.position && (
              <GraphTooltip
                chartRef={riskChartRef}
                visibility={tooltipVisible}
                handelTooltip={handelTooltip}
                setTooltipVisible={setTooltipVisible}
              />
            )}
            {showMarkerTooltip && (
              <MarkerTooltip
                chartRef={riskChartRef.current}
                name={CHART_NAMES.RISK}
                setClickAwayEnabled={setClickAwayEnabled}
              />
            )}
            <Box display={'flex'} justifyContent={'space-between'}>
              <Box visibility={displayLegend ? '' : 'hidden'} width={{md: '65%', sm: '55%', xs: '55%'}}>
                <div id={`legend-container-${CHART_NAMES.RISK}`} />
              </Box>
              <ChartActionButtons
                displayLegend={displayLegend}
                zoomProps={zoomProps}
                chartRef={riskChartRef}
                setClickAwayEnabled={setClickAwayEnabled}
              />
            </Box>
          </Box>
        )
    }
  }
  const [openRiskStateDropdown, setOpenRiskStateDropdown] = useState(false)
  const [openRiskCountyDropdown, setOpenRiskCountyDropdown] = useState(false)
  const [openHealthDropdown, setOpenHealthDropdown] = useState(false)

  const handleRiskStateDropdownClose = () => {
    setOpenRiskStateDropdown(false)
  }

  const handleRiskStateDropdownOpen = () => {
    setOpenRiskStateDropdown(true)
  }

  const handleRiskCountyDropdownClose = () => {
    setOpenRiskCountyDropdown(false)
  }

  const handleRiskCountyDropdownOpen = () => {
    setOpenRiskCountyDropdown(true)
  }
  const handleHealthDropdownClose = () => {
    setOpenHealthDropdown(false)
  }

  const handleHealthDropdownOpen = () => {
    setOpenHealthDropdown(true)
  }

  const handleGetNameFromKey = (list, filterValue, key, filterValueName) => {
    if (!filterValue) {
      if (filterValueName === 'COUNTY_NAME') {
        return 'All Counties'
      }
      return 'Default'
    }
    return list?.find((item) => item[key] === filterValue)?.[filterValueName]
  }

  const stateName = handleGetNameFromKey(
    states?.longitudinalStateList,
    selectedState,
    'STATE_ABBREVIATION',
    'STATE_NAME'
  )
  const countyName = handleGetNameFromKey(
    statesList[0].currentRiskOverTimeCounties?.longitudinalCountyList,
    selectedCounty,
    'STATE_COUNTY_FIPS_CODE',
    'COUNTY_NAME'
  )
  const healthConditionName = handleGetNameFromKey(
    healthAndEquityConditions?.healthEquityConditonList,
    selectedHealthCondition,
    'HEALTH_EQUITY_CONDITION_KEY',
    'HEALTH_EQUITY_CONDITION_NAME'
  )

  return (
    <Box position='relative'>
      <div style={{display: 'flex', alignItems: 'center'}}>
        <ChartTitle title={title} showInfo={true} />
      </div>
      <Grid container spacing={2}>
        <StyledFormControl sx={{m: 2}}>
          <StyledSelect
            value={selectedState}
            data-testid='risk-chart-state-dropdown'
            onChange={handleChangeState}
            displayEmpty
            open={openRiskStateDropdown}
            onClose={handleRiskStateDropdownClose}
            onOpen={handleRiskStateDropdownOpen}
            inputProps={{
              'aria-label': openRiskStateDropdown
                ? 'risk-chart-state-select-dropdown-expanded'
                : `risk-chart-state-selected-${stateName}`,
              'aria-labelledby': 'risk-state',
              id: 'risk-state'
            }}
            MenuProps={MenuProps}
            renderValue={(selected) => {
              if (!selected) {
                return <span aria-label='state-select-label'>Select a state</span>
              }
              const state = states?.longitudinalStateList?.find((state) => state.STATE_ABBREVIATION === selected)
              return state ? state.STATE_NAME : null
            }}
          >
            {states?.longitudinalStateList?.map((state) => (
              <MenuItem key={state.STATE_ABBREVIATION} value={state.STATE_ABBREVIATION}>
                {state.STATE_NAME}
              </MenuItem>
            ))}
          </StyledSelect>
        </StyledFormControl>
        <StyledFormControl sx={{m: 2}} disabled={!selectedState} data-testid='risk-chart-county-dropdown'>
          <StyledSelect
            value={selectedCounty}
            open={openRiskCountyDropdown}
            onClose={handleRiskCountyDropdownClose}
            onOpen={handleRiskCountyDropdownOpen}
            onChange={handleChangeCounty}
            displayEmpty
            inputProps={{
              'aria-label': openRiskCountyDropdown
                ? 'risk-chart-county-select-dropdown-expanded'
                : `risk-chart-county-selected-${countyName}`,
              'aria-labelledby': 'risk-county',
              id: 'risk-county'
            }}
            MenuProps={MenuProps}
            renderValue={(selected) => {
              if (!selected) {
                if (selectedState !== '') return <span aria-label='All-Counties'>All Counties</span>
                return <span aria-label='county-select-label'>Select a county</span>
              }
              const county = statesList[0].currentRiskOverTimeCounties?.longitudinalCountyList?.find(
                (county) => county.STATE_COUNTY_FIPS_CODE === selected
              )
              return county ? county.COUNTY_NAME : null
            }}
          >
            <MenuItem key='' value=''>
              <span aria-label='All-Counties'>All Counties</span>
            </MenuItem>
            {statesList[0].currentRiskOverTimeCounties?.longitudinalCountyList?.map((county) => (
              <MenuItem key={county.STATE_COUNTY_FIPS_CODE} value={county.STATE_COUNTY_FIPS_CODE}>
                {county.COUNTY_NAME}
              </MenuItem>
            ))}
          </StyledSelect>
        </StyledFormControl>
        <StyledFormControl sx={{m: 2}} disabled={!selectedState} data-testid='riskchart-health-condition-dropdown'>
          <StyledSelect
            style={{width: '300px'}}
            value={selectedHealthCondition}
            onChange={handleChangeHealthCondition}
            open={openHealthDropdown}
            onClose={handleHealthDropdownClose}
            onOpen={handleHealthDropdownOpen}
            displayEmpty
            inputProps={{
              'aria-label': openHealthDropdown
                ? 'health-condition-select-dropdown-expanded'
                : `health-condition-selected-${healthConditionName}`,
              'aria-labelledby': 'health-condition',
              id: 'health-condition'
            }}
            MenuProps={MenuProps}
            renderValue={(selected) => {
              if (!selected) {
                return <span aria-label='health-condition-select-label'>Select Health Condition</span>
              }
              const condition = healthAndEquityConditions?.healthEquityConditonList?.find(
                (condition) => condition.HEALTH_EQUITY_CONDITION_KEY === selected
              )
              return condition ? condition.HEALTH_EQUITY_CONDITION_NAME : null
            }}
          >
            {!errorLoadingHealthAndEquityConditions &&
              !loadingHealthAndEquityConditions &&
              healthAndEquityConditions?.healthEquityConditonList?.map((condition) => (
                <MenuItem key={condition.HEALTH_EQUITY_CONDITION_KEY} value={condition.HEALTH_EQUITY_CONDITION_KEY}>
                  {condition.HEALTH_EQUITY_CONDITION_NAME}
                </MenuItem>
              ))}
          </StyledSelect>
        </StyledFormControl>
        <StyledBox>
          {selectedHealthCondition && (
            <ColorSlider
              data-testid='risk-chart-slider'
              onChangeCommit={handleSliderChangeCommit}
              onChange={handleSliderChange}
              value={sliderChangingValue}
            />
          )}
        </StyledBox>
        <ToggleButton
          toggleBtnOnSelectCallback={updateToggleRiskOverTime}
          showTable={showRiskOverTimeTable}
          disableToggleBtn={!displayLegend}
        />
      </Grid>

      <Box tabIndex={0}>
        {showLoader && <ChartLoadingIndicator isLoading={isLoading} onLoadComplete={() => setShowLoader(false)} />}
        {selectedHealthCondition && !showLoader && isError && (
          <ChartDataRefetchOnError onTryAgain={() => fetchChartData()} />
        )}
        {selectedHealthCondition !== '' && !showLoader && !isError && checkIsChartResponseEmpty(dataSets) && (
          <ChartBlankError />
        )}

        {conditionalRender()}
      </Box>
    </Box>
  )
}

export default RiskOverTimeComponent

/*
  Function Descriptions:
    - concatData: Combines two datasets into a single array.
    - checkDataSelected: Determines which datasets and legends to use based on selected state, county, and health condition.
    - generateDataSet: Prepares datasets, labels, and axis limits for the chart.
    - handleChangeState: Updates the selected state and resets the county selection.
    - handleChangeCounty: Updates the selected county.
    - handleChangeHealthCondition: Updates the selected health condition and chart title.
    - fetchChartData: Fetches chart data based on selected filters and updates chart variables.
    - handleSliderChangeCommit: Updates the slider value when the user finishes adjusting it.
    - handleSliderChange: Adjusts the slider value dynamically while the user interacts with it.
    - handelTooltip: Generates tooltip content for the chart based on selected data.
    - conditionalRender: Renders the chart or table based on the current state and data availability.
    - handleRiskStateDropdownClose/Open: Toggles the state dropdown visibility.
    - handleRiskCountyDropdownClose/Open: Toggles the county dropdown visibility.
    - handleHealthDropdownClose/Open: Toggles the health condition dropdown visibility.
    - handleGetNameFromKey: Retrieves a name from a list based on a key and filter value.
*/
